import { Spin } from "antd";
import { useTranslation } from "react-i18next";

import BaseButton from "@/components_v2/Button/BaseButton";
import PersonalDetailsStep from "./form-steps/PersonalDetailsStep";
import {
  PERSONAL_DETAILS_VALIDATION_FIELDS,
  registerAccountSchema,
} from "../utils/formValidation";
import {
  buildAccountResetValues,
  registerAccountDefaultValues,
} from "../utils/formInitialValues";
import { useRegisterSubmit } from "../hooks/useRegisterSubmit";
import { useRegisterForm } from "../hooks/useRegisterForm";
import { RegisterFormValues, SingleStepRegisterProps } from "../types";

import "../styles.css";

const SingleStepRegister = ({ useVerifyToken }: SingleStepRegisterProps) => {
  const { t } = useTranslation();
  const {
    form,
    data,
    isSuccess,
    isFetching,
    error,
    countryOptions,
    areaOfExpertiseOptions,
  } = useRegisterForm<RegisterFormValues>({
    useVerifyToken,
    schema: registerAccountSchema,
    defaultValues: registerAccountDefaultValues,
    buildResetValues: buildAccountResetValues,
  });
  const { submit, isLoading } = useRegisterSubmit(form);

  const handleSubmit = async () => {
    const isValid = await form.trigger(PERSONAL_DETAILS_VALIDATION_FIELDS);
    if (!isValid) return;

    const values = form.getValues();
    submit({
      token: values.token,
      user: values.user,
      profile: values.profile,
    });
  };

  if (isFetching) {
    return (
      <div className="flex justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!isSuccess) {
    return (
      <div className="text-center">
        {error?.code && (
          <h3 className="register__title">
            {t(`auth.errors.${error.code}`, { defaultValue: error.detail })}
          </h3>
        )}
      </div>
    );
  }

  return (
    <div className="register-single-step-container flex flex-col flex-1 gap-400">
      <PersonalDetailsStep
        form={form}
        countryOptions={countryOptions}
        areaOfExpertiseOptions={areaOfExpertiseOptions}
        title={t("auth.labels.accountTitle")}
        description={
          data?.organization?.name
            ? t("auth.labels.applicationAccountDescription", {
                organizationName: data.organization.name,
              })
            : t("auth.labels.accountDescription")
        }
      />

      <BaseButton isFullWidth loading={isLoading} onClick={handleSubmit}>
        {t("auth.labels.createAccount")}
      </BaseButton>
    </div>
  );
};

export default SingleStepRegister;
